import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn, ShieldCheck, ScrollText, Award, FileBadge } from "lucide-react";
import { certificates, credentialChips, type Certificate } from "@/data/content";
import { Reveal } from "@/components/Shared";
import { CornerMarks } from "@/components/Backdrop";

const chipIcons = [ShieldCheck, FileBadge, Award, ScrollText];

export default function CredentialsVault() {
    const [active, setActive] = useState<Certificate | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setActive(null);
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = active ? "hidden" : "";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [active]);

    return (
        <section
            id="credentials-vault"
            className="relative overflow-hidden bg-[#0B1626]"
            data-testid="recognitions-section"
        >
            {/* ledger rules + glows */}
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.5]"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(to bottom, rgba(255,255,255,0.05) 0 1px, transparent 1px 30px), repeating-linear-gradient(to right, rgba(255,255,255,0.03) 0 1px, transparent 1px 240px)",
                }}
            />
            <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#1D4F9C]/25 blur-[130px]" />
            <div className="pointer-events-none absolute -bottom-48 -left-40 h-[520px] w-[520px] rounded-full bg-[#A87B2F]/20 blur-[140px]" />

            <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                <div className="max-w-3xl">
                    <p className="flex items-center gap-3 font-tech text-[11px] font-semibold uppercase tracking-[0.3em] text-[#D9B36A]">
                        <span className="h-2 w-2 shrink-0 bg-[#A87B2F]" />
                        06 — Recognitions & Credentials
                    </p>
                    <h2 className="mt-5 font-display text-2xl font-semibold leading-[1.15] tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="recognitions-title">
                        Recognised. Registered.{" "}
                        <span className="italic text-[#D9B36A]">Verified.</span>
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-white/60">
                        Selected registrations, professional credentials and recognition — issued by
                        government and professional authorities.
                    </p>
                </div>

                {/* Certificate showcase */}
                <div className="mt-14 grid gap-8 lg:grid-cols-2">
                    {certificates.map((cert, i) => (
                        <Reveal key={cert.id} delay={i * 0.1}>
                            <button
                                type="button"
                                onClick={() => setActive(cert)}
                                data-testid={`certificate-open-${cert.id}`}
                                className="group block w-full text-left"
                            >
                                <div className="relative overflow-hidden border border-white/10 bg-[#0F1B2E]/[0.03] p-5 backdrop-blur-sm transition-all duration-500 hover:border-[#D9B36A]/50 hover:bg-[#0F1B2E]/[0.06]">
                                    <span className="pointer-events-none absolute left-0 top-0 z-10 flex items-center gap-2 bg-[#A87B2F] px-3 py-1.5 font-tech text-[9px] font-semibold uppercase tracking-[0.24em] text-[#0B1626]">
                                        <ShieldCheck className="h-3 w-3" /> {cert.kind}
                                    </span>
                                    {/* framed certificate image */}
                                    <div className="relative mt-8 overflow-hidden border border-white/10 bg-[#0B1626]">
                                        <img
                                            src={cert.image}
                                            alt={`${cert.title} — ${cert.holder}`}
                                            loading="lazy"
                                            className="aspect-[4/3] w-full object-cover object-top transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1626] via-transparent to-transparent opacity-70" />
                                        <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 border border-white/20 bg-[#0B1626]/70 px-3.5 py-2 font-tech text-[10px] uppercase tracking-[0.2em] text-white opacity-0 backdrop-blur-md transition-all duration-500 group-hover:opacity-100">
                                            <ZoomIn className="h-3.5 w-3.5 text-[#D9B36A]" /> View Certificate
                                        </span>
                                    </div>
                                    <div className="mt-5 flex items-start justify-between gap-4">
                                        <div>
                                            <h3 className="font-display text-xl font-semibold text-white">
                                                {cert.title}
                                            </h3>
                                            <p className="mt-1.5 font-tech text-[10px] uppercase tracking-[0.2em] text-[#D9B36A]">
                                                {cert.holder}
                                            </p>
                                            <p className="mt-3 text-sm leading-relaxed text-white/55">
                                                {cert.issuer}
                                            </p>
                                        </div>
                                    </div>
                                    <p className="mt-5 border-t border-white/10 pt-4 font-tech text-[10px] uppercase leading-loose tracking-[0.14em] text-white/45">
                                        {cert.meta}
                                    </p>
                                </div>
                            </button>
                        </Reveal>
                    ))}
                </div>

                {/* Credential ledger */}
                <Reveal delay={0.15}>
                    <div className="mt-8 grid gap-px overflow-hidden border border-white/10 bg-[#0F1B2E]/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="credential-ledger">
                        {credentialChips.map((chip, i) => {
                            const Icon = chipIcons[i % chipIcons.length];
                            return (
                                <div key={chip.label} className="group bg-[#0B1626] p-6 transition-colors duration-300 hover:bg-[#11213a]">
                                    <Icon className="h-5 w-5 text-[#D9B36A]" />
                                    <p className="mt-4 font-tech text-[10px] uppercase tracking-[0.18em] text-white/45">
                                        {chip.label}
                                    </p>
                                    <p className="mt-1.5 font-display text-base font-semibold text-white">
                                        {chip.value}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </Reveal>
            </div>

            {/* Lightbox */}
            <AnimatePresence>
                {active && (
                    <motion.div
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActive(null)}
                        data-testid="certificate-lightbox"
                    >
                        <div className="absolute inset-0 bg-[#050B14]/90 backdrop-blur-md" />
                        <motion.div
                            className="relative z-10 max-h-[92vh] w-full max-w-5xl overflow-hidden border border-white/15 bg-[#0B1626]"
                            initial={{ scale: 0.94, y: 20, opacity: 0 }}
                            animate={{ scale: 1, y: 0, opacity: 1 }}
                            exit={{ scale: 0.96, y: 10, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <CornerMarks color="#D9B36A" />
                            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
                                <div>
                                    <p className="font-tech text-[9px] uppercase tracking-[0.24em] text-[#D9B36A]">
                                        {active.kind}
                                    </p>
                                    <p className="mt-1 font-display text-lg font-semibold text-white">
                                        {active.title}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActive(null)}
                                    data-testid="certificate-close"
                                    aria-label="Close certificate viewer"
                                    className="border border-white/15 p-2.5 text-white/70 transition-colors duration-300 hover:border-[#D9B36A] hover:text-[#D9B36A]"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="max-h-[74vh] overflow-auto bg-[#050B14] p-4 sm:p-8">
                                <img
                                    src={active.image}
                                    alt={`${active.title} — ${active.holder}`}
                                    className="mx-auto w-full max-w-3xl border border-white/10 shadow-2xl"
                                />
                            </div>
                            <p className="border-t border-white/10 px-6 py-4 font-tech text-[10px] uppercase tracking-[0.14em] text-white/45">
                                {active.holder} &middot; {active.meta}
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
